/* ---------------------------------------------------------------------
   The listing page, narrowed to a quiz answer.

   A rail's "View All" tile used to go to the plain collection page: the
   shopper answered a question, watched five products change to suit them,
   then followed the way out and landed back among everything. This closes
   that — the tile carries the answer as ?vro=<facet>:<value> (see
   assets/vro-subfilter.js) and this rebuilds the grid around it.

   The pool is fetched, not filtered in place. Narrowing the 24 products
   Liquid happened to render on page one would answer "which of these 24
   suit you", which is not the question — so this walks the whole shelf
   through templates/collection.vro-personalize.liquid, the same fragment
   the home-page rails rebuild from, and ranks all of it. Native pagination
   is hidden while that view is on, since the pool has already covered it.

   Scoring is VroPersonalization.facetScore, unchanged: a product has to
   say it suits this type, or claim a benefit that addresses it. Nothing
   here decides relevance on its own.

   Everything is reversible. Retake drops the parameter and reloads, which
   is the plain listing again — the server-rendered grid is never thrown
   away, only replaced in the DOM.
--------------------------------------------------------------------- */
(function () {
  'use strict';

  /* The View All tile at a product card's size. Sharing a row it already
     stretches to the row; alone in the last row it would shrink to its
     words, so it takes the height of the card before it, and again on
     resize. */
  function sizeLikeCard(tile) {
    var grid = tile.parentNode;
    if (!grid) return;
    var cards = grid.querySelectorAll('.feed-card');
    var card = cards.length ? cards[cards.length - 1] : null;
    if (!card) return;
    var h = card.getBoundingClientRect().height;
    if (h > 0) tile.style.minHeight = Math.round(h) + 'px';
  }

  var MAX_PAGES = 6;               /* 50 a page — enough for this shop's biggest shelf */

  function root() { return document.querySelector('[data-vro-plp]'); }

  /* ?vro=skin:oily  ->  { facet: 'skin', value: 'oily' } */
  function asked() {
    var raw = new URLSearchParams(window.location.search).get('vro');
    if (!raw) return null;
    var at = raw.indexOf(':');
    if (at < 1) return null;
    var facet = raw.slice(0, at).trim();
    var value = raw.slice(at + 1).trim();
    return (facet && value) ? { facet: facet, value: value } : null;
  }

  function grids(scope) {
    return Array.prototype.slice.call(scope.querySelectorAll('.clp-grid'));
  }

  function api() {
    return window.VroPersonalization;
  }

  /* One bar, two callers: the ?vro= arrival above, whose Retake drops the
     parameter and reloads, and the in-page quiz below, whose Retake puts
     the server's own products back without a round trip. Only that last
     step differs, so it is passed in rather than assumed. */
  function noted(bar, title, detail, count, total, onRetake) {
    if (!bar) return;
    bar.innerHTML = '';

    var tick = document.createElement('span');
    tick.className = 'vro-noted-check';
    tick.innerHTML = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
      '<path d="M7.4 12.7 10.1 15.4 16.6 7.9" stroke="currentColor" stroke-width="2" ' +
      'stroke-linecap="round" stroke-linejoin="round"/></svg>';
    bar.appendChild(tick);

    var text = document.createElement('span');
    text.className = 'vro-noted-text';

    var t = document.createElement('span');
    t.className = 'vro-noted-title';
    t.textContent = title;
    text.appendChild(t);

    var d = document.createElement('span');
    d.className = 'vro-noted-detail';
    d.textContent = detail;
    text.appendChild(d);

    /* Retake sits inside the bar, under the line it undoes — the same
       place it sits on every question row in the theme. */
    var retake = document.createElement('button');
    retake.type = 'button';
    retake.className = 'vro-reselect vro-reselect--noted is-visible';
    retake.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v5h5"/></svg><span>Retake</span>';
    retake.addEventListener('click', onRetake);
    text.appendChild(retake);

    bar.appendChild(text);

    if (count != null) {
      var c = document.createElement('span');
      c.className = 'info-pill-count';
      c.textContent = count + ' from ' + total;
      bar.appendChild(c);
    }

    bar.hidden = false;
  }

  /* The denominator every bar on this page prints: the collection's real
     product count, stamped on the section by Liquid
     (data-vro-plp-total), not the slice this script scored. A listing of
     290 read one page deep used to say "6 from 24". */
  function shelfTotal(scope, fallback) {
    var said = scope && parseInt(scope.getAttribute('data-vro-plp-total') || '', 10);
    if (said > 0) return said;
    return fallback;
  }

  function render(scope, cards) {
    var all = grids(scope);
    if (!all.length) return;

    /* One grid from here on: the server splits it in two around the
       mid-grid banner, which only makes sense for the listing it was
       paginated for. */
    var first = all[0];
    all.slice(1).forEach(function (g) {
      var wrap = g.closest('.clp-container') || g;
      wrap.parentNode.removeChild(wrap);
    });

    first.innerHTML = '';
    cards.forEach(function (card) { first.appendChild(card); });

    var pagination = scope.querySelector('.clp-pagination');
    if (pagination) pagination.hidden = true;

    if (window.VroWishlist && window.VroWishlist.sync) window.VroWishlist.sync();
  }

  function empty(scope, value) {
    var bar = scope.querySelector('[data-vro-plp-noted]');
    if (bar) bar.hidden = true;
    /* Nothing matched, so the plain listing is the better answer — it is
       already on screen, and saying so is more use than an empty grid. */
    if (window.console && console.info) {
      console.info('[vro-plp] nothing in this collection matches "' + value + '"; showing the full listing');
    }
  }

  function run() {
    var scope = root();
    if (!scope) return;

    var ask = asked();
    if (!ask) return;

    var vro = api();
    if (!vro || !vro.pool || !vro.facetScore) return;

    var handle = scope.getAttribute('data-vro-plp-collection');
    if (!handle) return;

    scope.classList.add('vro-plp-loading');

    vro.pool({ collection: handle, filters: true }, MAX_PAGES).then(function (cards) {
      var ranked = [];
      cards.forEach(function (card) {
        var score = vro.facetScore(card, ask.facet, ask.value);
        if (score > 0) ranked.push({ card: card, score: score });
      });
      ranked.sort(function (a, b) { return b.score - a.score; });

      scope.classList.remove('vro-plp-loading');

      if (!ranked.length) { empty(scope, ask.value); return; }

      render(scope, ranked.map(function (hit) { return hit.card; }));

      /* The Filters counts: what this answer keeps of the whole shelf,
         with the applied filters worked out on top
         (assets/vro-facet-count.js), so each count is what ticking it
         would show here. */
      if (window.VroFacetCount) {
        vro.pool({ collection: handle }, MAX_PAGES).then(function (all) {
          var allow = {};
          all.forEach(function (card) {
            var h = card.getAttribute('data-vro-handle');
            if (h && vro.facetScore(card, ask.facet, ask.value) > 0) allow[h] = true;
          });
          window.VroFacetCount.paint({ base: handle, allow: allow });
        }).catch(function () { /* Shopify's counts stay */ });
      }

      var shelf = scope.getAttribute('data-vro-plp-title') || 'this collection';
      var total = shelfTotal(scope, cards.length);
      var detail = ranked.length === total
        ? 'Everything in ' + shelf + ' suits it — closest match first.'
        : ranked.length + ' of ' + total + ' in ' + shelf + ' suit it, closest match first.';

      noted(scope.querySelector('[data-vro-plp-noted]'),
        'Noted — ' + ask.value, detail, ranked.length, total,
        function () {
          var url = new URL(window.location.href);
          url.searchParams.delete('vro');
          window.location.href = url.toString();
        });
    }).catch(function () {
      /* The listing the server rendered is still on screen and still
         correct, just not narrowed. That is a worse answer than the one
         asked for, but it is an answer — and never a broken page. */
      scope.classList.remove('vro-plp-loading');
    });
  }

  /* ------------------------------------------------------------------
     The quiz answered on this page.

     snippets/sort-concern-card.liquid offers its four answers as a
     cancelable event before it draws anything. Claiming it here is what
     stops the card showing a list of picks of its own: on a listing page
     the answer belongs in the listing, so it rewrites the products below
     the second banner instead of telling the same answer twice.

     Everything above the banner is left alone. That half of the grid is
     what the shopper was reading when the question was put to them, and
     rewriting it would move the page under their hands.

     The pool is fetched from THIS collection, not from the shelf the
     category maps to. The shopper is standing in Grooming & Care and
     asked about skin — the answer is which of these products suit them,
     not which products anywhere do.
  ------------------------------------------------------------------ */

  function quizGrid(scope) {
    /* Without a mid-grid banner the server never opened a second grid, so
       there is nothing "below" it and the single grid on the page is the
       only thing an answer can change. */
    return scope.querySelector('[data-vro-plp-quiz-grid]') || grids(scope).pop() || null;
  }

  function quizBar(scope) {
    return scope.querySelector('[data-vro-plp-quiz-noted]') ||
           scope.querySelector('[data-vro-plp-noted]');
  }

  /* The server's own products, held aside so Retake is a restore rather
     than a reload. Taken once: after the first answer the grid holds
     fetched cards, and saving those would make Retake a no-op. */
  function keep(grid) {
    if (!grid.__vroOriginal) {
      grid.__vroOriginal = Array.prototype.slice.call(grid.children);
    }
    return grid.__vroOriginal;
  }

  /* ---------- A way on from the end of a narrowed grid -------------------

     Native pagination is hidden while an answer is on: the pool already
     walked the shelf, so the server's page links point at a listing this
     grid no longer shows. That left the last row of products with nothing
     after it at all — the grid simply stopped.

     So the narrowed grid ends with the way back to the whole shelf, which
     is this page without the narrowing and with its own paging intact.
     Different from Retake on purpose: Retake is about the question, this
     is about the products. */
  var VIEW_ALL_ICON =
    '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M9 3.5c3 0 5.5 1.8 6.5 4.3l4-1.3-1.8 3.9c1 .9 1.6 2 1.6 3.3 0 3.3-4.6 6-9.6 6-5.2 0-9.7-3-9.7-6.7C0 9.2 3.9 3.5 9 3.5Z"/>' +
    '<circle cx="6.5" cy="12.5" r="1.2" fill="currentColor" stroke="none"/></svg>';

  /* The same tile every rail closes with — .feed-view-all, to the markup
     sections/main-collection.liquid already writes for its curation rail,
     so this is the card shape a shopper has met all over the site rather
     than a button of its own invention.

     It goes IN the grid as the last cell, not under it: the grid is what
     the answer changed, and the way on belongs at the end of it. Nothing
     removes it — restore() replaces the grid's children with the ones it
     kept before the first answer, and the tile was never among them. */
  function viewAll(scope, grid) {
    var tile = document.createElement('a');
    tile.className = 'feed-view-all clp-grid-view-all';
    tile.setAttribute('data-vro-plp-viewall', '');
    /* This page without its query: ?vro= would only narrow it again. */
    tile.href = window.location.pathname;

    var icon = document.createElement('span');
    icon.className = 'feed-view-all-icon';
    icon.innerHTML = VIEW_ALL_ICON;
    tile.appendChild(icon);

    var text = document.createElement('span');
    text.className = 'feed-view-all-text';

    var label = document.createElement('span');
    label.className = 'feed-view-all-label';
    label.textContent = 'View All';
    text.appendChild(label);

    var category = document.createElement('span');
    category.className = 'feed-view-all-category';
    category.textContent = scope.getAttribute('data-vro-plp-title') || 'Everything';
    text.appendChild(category);

    tile.appendChild(text);
    grid.appendChild(tile);
    sizeLikeCard(tile);
    window.addEventListener('resize', function () { sizeLikeCard(tile); });
  }

  function restore(scope, grid, bar) {
    if (grid.__vroOriginal) {
      grid.innerHTML = '';
      grid.__vroOriginal.forEach(function (card) { grid.appendChild(card); });
    }
    /* The arrival animation is cleared with the bar, so the next answer
       plays it again rather than the class already being there. */
    if (bar) { bar.hidden = true; bar.innerHTML = ''; bar.classList.remove('vro-noted-arrive'); }
    var pager = scope.querySelector('.clp-pagination');
    if (pager) pager.hidden = false;
    if (window.VroWishlist && window.VroWishlist.sync) window.VroWishlist.sync();
  }

  /* ---------- The question folds away, the answer takes its place -------

     VroPersonalization.collapse fades the card and then pulls it from the
     flow, which drops everything below by a card's height in one frame.
     Here the bar arrives in the space the card leaves, so the height is
     animated too and nothing jumps out from under the cursor.

     `auto` is not a height CSS can transition from or to, so both
     directions measure first and animate between two pixel values.
  --------------------------------------------------------------------- */

  /* Matches the .5s fold in vro-personalization.css, with a little over
   for the browser to finish it — a backstop that fires first cuts the
   animation off half way. */
var ROLL_MS = 560;              /* the .32s transition, plus a little */

  /* The whole card, not the quiz root inside it: rolling the root would
     leave the section's own 32px padding behind as a gap. */
  function quizBox(origin) {
    return origin ? (origin.closest('.clp-quiz') || origin) : null;
  }

  function afterRoll(box, settle) {
    var onEnd = function (e) {
      if (e.target !== box || e.propertyName !== 'height') return;
      finish();
    };
    var finish = function () {
      clearTimeout(box.__vroRollTimer);
      box.__vroRollTimer = null;
      box.removeEventListener('transitionend', onEnd);
      settle();
    };
    box.addEventListener('transitionend', onEnd);
    /* transitionend does not fire where the motion is off, or where the
       height never actually changed. */
    box.__vroRollTimer = setTimeout(finish, ROLL_MS);
  }

  function rollUp(box, after) {
    if (!box || box.classList.contains('vro-quiz-rolled')) { after(); return; }
    clearTimeout(box.__vroRollTimer);

    var h = box.getBoundingClientRect().height;
    box.classList.add('vro-quiz-roll');
    box.style.height = h + 'px';
    void box.offsetHeight;                    /* commit the start value */
    box.classList.add('vro-quiz-rolled');
    box.style.height = '0px';

    afterRoll(box, after);
  }

  function rollDown(box) {
    if (!box || !box.classList.contains('vro-quiz-rolled')) return;
    clearTimeout(box.__vroRollTimer);

    /* Measure the height it will settle at with the transition off, so the
       measurement itself is never seen. */
    box.style.transition = 'none';
    box.classList.remove('vro-quiz-rolled');
    box.style.height = 'auto';
    var h = box.getBoundingClientRect().height;
    box.classList.add('vro-quiz-rolled');
    box.style.height = '0px';
    void box.offsetHeight;
    box.style.transition = '';

    box.classList.remove('vro-quiz-rolled');
    box.style.height = h + 'px';

    afterRoll(box, function () {
      /* Back to a card that sizes itself, so it can grow with the flow
         once the shopper starts answering again. */
      box.style.height = '';
      box.classList.remove('vro-quiz-roll');
    });
  }

  /* ---------- The phone starts the same quiz with a picture -------------

     The mobile comp opens with "Where do you want to start?" and three
     tiles, where the desktop comp opens with the concern card. They are
     not two quizzes: the tile is the first question, and the rest of the
     flow — the concern, the condition, the budget, the fold, the bar, the
     narrowed grid — is the one the desktop card already runs.

     So a tile hands its department to that card rather than navigating to
     a collection. A tile naming something the quiz has no department for
     keeps its link and goes where it always went: "Beard" has no category
     in assets/vro-personalization.js, and sending someone into a skin
     quiz because the words are adjacent would be worse than the listing
     they asked for.
  --------------------------------------------------------------------- */

  function phoneQuiz(scope) { return scope.querySelector('.clp-quiz--phone'); }
  function webQuiz(scope) { return scope.querySelector('.clp-quiz--web'); }

  function categoryForTile(vro, tile) {
    var label = tile.querySelector('.clp-quiz-option-label');
    var byName = (vro.quiz && vro.quiz.keyFor)
      ? vro.quiz.keyFor(label ? label.textContent : '')
      : null;
    if (byName) return byName;

    /* Second chance: the shelf the merchant pointed the tile at. "Face"
       resolves by name, but a tile renamed to something local still lands
       if it points at a department's own collection. */
    var href = tile.getAttribute('href') || '';
    var found = href.match(/collections\/([^/?#]+)/);
    if (!found || !vro.categories) return null;
    for (var name in vro.categories) {
      if (!vro.categories.hasOwnProperty(name)) continue;
      if (vro.categories[name].collection === found[1]) return name;
    }
    return null;
  }

  /* The card is display:none on a phone until the picture hands over. It
     opens folded and unfolds, so the tiles and the questions are one
     movement rather than a swap. */
  function offeredCategories(scope) {
    var card = scope.querySelector('.clp-quiz--web [data-quiz-root]');
    var raw = card ? (card.getAttribute('data-quiz-categories') || '') : '';
    return raw.split(',').map(function (n) { return n.trim(); }).filter(Boolean);
  }

  function problemForTile(vro, category, tile) {
    var label = tile.querySelector('.clp-quiz-option-label');
    var word = (label ? label.textContent : '').trim().toLowerCase();
    if (!word) return null;
    var list = (vro.quiz && vro.quiz.problemsFor) ? vro.quiz.problemsFor(category) : [];
    for (var i = 0; i < list.length; i++) {
      if (String(list[i]).toLowerCase().indexOf(word) > -1) return list[i];
    }
    return null;
  }

  function openWebQuiz(scope, category, problem) {
    var web = webQuiz(scope);
    if (!web) return false;

    web.classList.add('vro-quiz-open', 'vro-quiz-roll', 'vro-quiz-rolled');
    web.style.height = '0px';
    rollDown(web);

    /* [data-quiz-root] is the element snippets/sort-concern-card.liquid
       listens on — the same node it later reports its answers from. */
    var card = web.querySelector('[data-quiz-root]');
    if (card) {
      card.dispatchEvent(new CustomEvent('vro:quiz:start', {
        bubbles: false, detail: { category: category, problem: problem || null }
      }));
    }
    return true;
  }

  document.addEventListener('click', function (e) {
    var tile = e.target.closest ? e.target.closest('.clp-quiz--phone .clp-quiz-option') : null;
    if (!tile) return;

    var scope = root();
    var vro = api();
    if (!scope || !vro || !scope.contains(tile)) return;

    var category = categoryForTile(vro, tile);
    var problem = null;
    /* A page whose quiz asks about one department only (Health & Nutrition
       asks about Health & Energy) takes any tile as the start of that quiz,
       with the concern the tile names already picked where one matches
       ("Energy" -> "Low energy", "Sleep" -> "Poor sleep"). The tile's link
       is only the fallback for a page with no quiz to hand over to. */
    if (!category) {
      var only = offeredCategories(scope);
      if (only.length === 1) {
        category = only[0];
        problem = problemForTile(vro, category, tile);
      }
    }
    if (!category) return;                 /* keeps its link */

    var phone = phoneQuiz(scope);
    if (!webQuiz(scope)) return;

    e.preventDefault();
    scope.__vroPhoneBox = phone;
    rollUp(phone, function () { openWebQuiz(scope, category, problem); });
  });

  /* Retake goes back to wherever the shopper started: the picture on a
     phone, the card on a desktop. */
  function reopenQuiz(scope, box) {
    var phone = scope.__vroPhoneBox;
    if (!phone) { rollDown(box); return; }

    var web = webQuiz(scope);
    if (web) {
      web.classList.remove('vro-quiz-open', 'vro-quiz-roll', 'vro-quiz-rolled');
      web.style.height = '';
    }
    scope.__vroPhoneBox = null;
    rollDown(phone);
  }

  /* ---------- The answer, directly under the question -----------------

     The answer used to replace every product below the mid-grid banner:
     the listing a shopper was browsing changed under them, native paging
     disappeared and the grid ended on a lone "View All" tile. The answer
     is its own block now, in the space the question folds out of — a
     Noted bar and a row of the theme's own product cards, the same shape
     as the curation rail above it — and the listing below is never
     touched.

     Five picks and a View All tile (two full rows of three on a phone,
     one row of six on a desktop). View All opens the rest of the matches
     in place rather than leaving the page.
  --------------------------------------------------------------------- */
  var PICKS_SHOWN = 5;

  function picksBlock(scope, box) {
    var block = scope.__vroPicks;
    if (block && block.isConnected) return block;
    block = document.createElement('div');
    block.className = 'clp-container vro-plp-picks';
    block.setAttribute('data-vro-plp-picks', '');
    var bar = document.createElement('div');
    bar.className = 'vro-noted-pill vro-plp-noted';
    bar.hidden = true;
    block.appendChild(bar);
    var rail = document.createElement('div');
    rail.className = 'clp-curation-rail vro-plp-picks-rail';
    block.appendChild(rail);
    var anchor = box || quizGrid(scope);
    if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(block, anchor.nextSibling);
    else scope.appendChild(block);
    scope.__vroPicks = block;
    return block;
  }

  function picksTile(scope, rail, extra) {
    var tile = document.createElement('button');
    tile.type = 'button';
    tile.className = 'feed-view-all clp-curation-view-all vro-plp-picks-more';
    var icon = document.createElement('span');
    icon.className = 'feed-view-all-icon';
    icon.innerHTML = VIEW_ALL_ICON;
    tile.appendChild(icon);
    var text = document.createElement('span');
    text.className = 'feed-view-all-text';
    var label = document.createElement('span');
    label.className = 'feed-view-all-label';
    label.textContent = 'View All';
    text.appendChild(label);
    var sub = document.createElement('span');
    sub.className = 'feed-view-all-category';
    sub.textContent = extra + ' more ' + (extra === 1 ? 'match' : 'matches');
    text.appendChild(sub);
    tile.appendChild(text);
    tile.addEventListener('click', function () {
      var motion = window.VroMotion;
      var open = function () {
        rail.classList.add('is-expanded');
        var more = Array.prototype.slice.call(rail.querySelectorAll('.feed-card[hidden]'));
        more.forEach(function (card) { card.hidden = false; });
        /* The rest of the answer pops in the way the first of it did. */
        if (window.VroMotion && window.VroMotion.pop) {
          window.VroMotion.pop(more);
        } else {
          more.forEach(function (card, n) {
            card.style.animationDelay = (Math.min(n, 8) * 45) + 'ms';
            card.classList.add('vro-settle-in');
          });
        }
        tile.remove();
      };
      if (!motion) { open(); return; }
      tile.disabled = true;
      motion.preload(rail.querySelectorAll('.feed-card[hidden]'), { limit: 12, timeout: 1200 }).then(function () {
        motion.morphHeight(rail, open, { duration: 480 });
      });
    });
    return tile;
  }

  function fillPicks(rail, cards) {
    rail.classList.remove('is-expanded');
    rail.innerHTML = '';
    cards.forEach(function (card, i) {
      card.classList.remove('vro-settle-in');
      if (i >= PICKS_SHOWN) {
        card.hidden = true;
      } else {
        card.hidden = false;
      }
      rail.appendChild(card);
    });
    /* The answer pops in, as on every quiz (assets/vro-motion.js). */
    var shown = cards.filter(function (card) { return !card.hidden; });
    if (window.VroMotion && window.VroMotion.pop) {
      window.VroMotion.pop(shown);
    } else {
      shown.forEach(function (card, i) {
        card.style.animationDelay = (i * 45) + 'ms';
        card.classList.add('vro-settle-in');
      });
    }
  }

  /* ---------- The answer in place of the curation row ------------------

     On a category page the quiz sits inside the curation section ("Style
     it with", "Clean protein"), above its tabbed row of products. The
     answer belongs exactly there: the row the shopper was looking at is
     replaced by the matches, and the section's insight pill ("Sweat-wicking
     fabrics, picked for Mumbai humidity") becomes the Noted box. Retake
     puts the merchant's pill, tabs and row back as they were. */
  function morphBox(el, mutate) {
    var motion = window.VroMotion;
    if (motion && el) motion.morphHeight(el, mutate, { duration: 480 });
    else mutate();
  }

  function inCuration(scope, box, res, line, retake) {
    var cur = box && box.closest ? box.closest('.clp-curation') : null;
    if (!cur) return false;
    var wrap = cur.querySelector('.clp-container') || cur;
    var head = cur.querySelector('.clp-curation-head');

    var pill = cur.querySelector('.clp-curation-why');
    var madePill = false;
    if (!pill && head) {
      pill = document.createElement('div');
      pill.className = 'clp-curation-why';
      head.appendChild(pill);
      madePill = true;
    }
    if (pill && !('__vroPillOriginal' in pill)) pill.__vroPillOriginal = pill.innerHTML;

    var panel = cur.querySelector('.vro-plp-picks-panel');
    if (!panel) {
      panel = document.createElement('div');
      panel.className = 'clp-curation-panel is-active vro-plp-picks-panel';
      panel.setAttribute('data-vro-plp-picks', '');
      var firstPanel = cur.querySelector('.clp-curation-panel');
      if (firstPanel && firstPanel.parentNode) firstPanel.parentNode.insertBefore(panel, firstPanel);
      else wrap.appendChild(panel);
    }
    panel.innerHTML = '';
    var rail = document.createElement('div');
    rail.className = 'clp-curation-rail vro-plp-picks-rail';
    panel.appendChild(rail);

    var matched = res.picked.length > 0;
    var shelf = scope.getAttribute('data-vro-plp-title') || 'this collection';

    morphBox(wrap, function () {
      cur.classList.add('vro-plp-picks-on');
      if (pill) {
        if (matched) {
          noted(pill, line.title, line.detail, res.picked.length,
            shelfTotal(scope, res.cards.length), retake);
        } else {
          noted(pill, line.title,
            'Nothing in ' + shelf + ' matches all of that yet, so the row below is unchanged.',
            null, null, retake);
        }
        pill.classList.add('vro-noted', 'vro-noted-pill', 'vro-noted-enter');
        setTimeout(function () { pill.classList.remove('vro-noted-enter'); }, 500);
      }
      if (matched) {
        fillPicks(rail, res.picked);
        if (res.picked.length > PICKS_SHOWN) {
          rail.appendChild(picksTile(scope, rail, res.picked.length - PICKS_SHOWN));
        }
      } else {
        /* No matches: leave the merchant's row showing. */
        cur.classList.remove('vro-plp-picks-on');
        panel.remove();
      }
    });
    if (window.VroWishlist && window.VroWishlist.sync) window.VroWishlist.sync();

    scope.__vroPicksUndo = function () {
      morphBox(wrap, function () {
        cur.classList.remove('vro-plp-picks-on');
        var p = cur.querySelector('.vro-plp-picks-panel');
        if (p) p.remove();
        if (pill) {
          if (madePill) {
            pill.remove();
          } else {
            pill.innerHTML = pill.__vroPillOriginal;
            pill.classList.remove('vro-noted', 'vro-noted-pill', 'vro-noted-enter');
            pill.hidden = false;
            pill.classList.add('vro-pill-back');
            setTimeout(function () { pill.classList.remove('vro-pill-back'); }, 450);
          }
        }
      });
    };
    return cur;
  }

  function dropPicks(scope) {
    if (scope.__vroPicksUndo) {
      var undo = scope.__vroPicksUndo;
      scope.__vroPicksUndo = null;
      undo();
      return;
    }
    var block = scope.__vroPicks;
    if (!block) return;
    scope.__vroPicks = null;
    var motion = window.VroMotion;
    if (!motion || motion.reduced() || !block.isConnected) { block.remove(); return; }
    var h = block.getBoundingClientRect().height;
    block.style.overflow = 'hidden';
    block.style.height = h + 'px';
    void block.offsetHeight;
    block.style.transition = 'height .5s cubic-bezier(.32, .06, .2, 1), opacity .3s ease';
    block.style.height = '0px';
    block.style.opacity = '0';
    setTimeout(function () { block.remove(); }, 540);
  }

  function answerInGrid(scope, detail) {
    var vro = api();
    if (!vro || !vro.pool || !vro.scoreCard || !vro.categoryFor) return false;
    var handle = scope.getAttribute('data-vro-plp-collection');
    if (!handle) return false;
    var answers = detail.answers;
    var category = vro.categoryFor(answers && answers.category);
    if (!category) return false;

    /* Claimed from here on: every path below leaves the shopper with a
       page that answers them, so the card can stop holding the flow. */
    var box = quizBox(detail.origin);
    if (box) box.classList.add('vro-plp-quiz-waiting');
    var restart = function () {
      if (typeof detail.restart === 'function') detail.restart();
    };
    var motion = window.VroMotion;

    vro.pool({ collection: handle, filters: true }, MAX_PAGES).then(function (cards) {
      var scored = [];
      var seen = {};
      cards.forEach(function (card, i) {
        var h = card.getAttribute('data-vro-handle');
        if (h && seen[h]) return;
        if (h) seen[h] = true;
        var hit = vro.scoreCard(card, answers, category);
        if (hit) { hit.order = i; scored.push(hit); }
      });
      scored.sort(function (a, b) { return b.score - a.score || a.order - b.order; });
      var picked = scored.map(function (hit) { return hit.card; });
      /* Photos first, so the row never lands blank. */
      var ready = (motion && picked.length) ? motion.preload(picked.slice(0, PICKS_SHOWN)) : Promise.resolve();
      return ready.then(function () { return { cards: cards, picked: picked }; });
    }).then(function (res) {
      if (box) box.classList.remove('vro-plp-quiz-waiting');
      var line = vro.noted ? vro.noted(answers) : { title: 'Noted', detail: '' };
      var shelf = scope.getAttribute('data-vro-plp-title') || 'this collection';
      var matched = res.picked.length > 0;

      /* Retake takes the answer away and asks again. The listing below was
         never changed, so there is nothing of it to put back. */
      var retake = function () {
        dropPicks(scope);
        reopenQuiz(scope, box);
      };

      /* The question folds away first, and the answer grows into the space
         it leaves — one movement. The card is reset while folded, so Retake
         unfolds a fresh question rather than the spent one. */
      rollUp(box, function () {
        restart();
        var cur = inCuration(scope, box, res, line, retake);
        if (cur) {
          if (vro.scrollTo) vro.scrollTo(cur);
          return;
        }
        var block = picksBlock(scope, box);
        var bar = block.querySelector('.vro-plp-noted');
        var rail = block.querySelector('.vro-plp-picks-rail');
        var build = function () {
          if (matched) {
            noted(bar, line.title, line.detail, res.picked.length,
              shelfTotal(scope, res.cards.length), retake);
            fillPicks(rail, res.picked);
            if (res.picked.length > PICKS_SHOWN) {
              rail.appendChild(picksTile(scope, rail, res.picked.length - PICKS_SHOWN));
            }
            rail.hidden = false;
            if (window.VroWishlist && window.VroWishlist.sync) window.VroWishlist.sync();
          } else {
            noted(bar, line.title,
              'Nothing in ' + shelf + ' matches all of that yet — the full listing is below.',
              null, null, retake);
            rail.innerHTML = '';
            rail.hidden = true;
          }
          bar.classList.remove('vro-noted-arrive');
          void bar.offsetHeight;
          bar.classList.add('vro-noted-arrive');
        };
        block.style.height = '';
        block.style.opacity = '';
        block.style.transition = '';
        if (motion) {
          /* Grows from nothing, so the listing below eases down. */
          block.style.height = '0px';
          block.style.overflow = 'hidden';
          void block.offsetHeight;
          build();
          block.style.height = '';
          var to = block.getBoundingClientRect().height;
          block.style.height = '0px';
          void block.offsetHeight;
          block.style.transition = 'height .5s cubic-bezier(.22, .9, .3, 1)';
          block.style.height = to + 'px';
          setTimeout(function () {
            block.style.height = '';
            block.style.overflow = '';
            block.style.transition = '';
          }, 540);
        } else {
          build();
        }
        if (vro.scrollTo) vro.scrollTo(block);
      });
    }).catch(function () {
      /* The listing is still on screen and still correct — an answer, and
         never a broken page. The card stays open, since there is nothing
         for it to have folded away for. */
      if (box) box.classList.remove('vro-plp-quiz-waiting');
      restart();
    });
    return true;
  }

  /* The shelf is fetched as soon as the shopper starts answering, so by the
     last answer it is already in VroPersonalization's cache and the result
     arrives straight after the question folds. */
  document.addEventListener('click', function (e) {
    var inQuiz = e.target.closest ? e.target.closest('.clp-quiz') : null;
    if (!inQuiz) return;
    var scope = root();
    var vro = api();
    if (!scope || scope.__vroPoolWarm || !vro || !vro.pool || !scope.contains(inQuiz)) return;
    var handle = scope.getAttribute('data-vro-plp-collection');
    if (!handle) return;
    scope.__vroPoolWarm = true;
    vro.pool({ collection: handle, filters: true }, MAX_PAGES).catch(function () { scope.__vroPoolWarm = false; });
  }, true);

  document.addEventListener('vro:quiz:results', function (e) {
    var scope = root();
    if (!scope || !e.detail) return;
    /* Only the quiz standing in this listing. A modal opened from the nav
       is asking on behalf of the whole shop, not this shelf. */
    if (e.detail.origin && !scope.contains(e.detail.origin)) return;
    if (answerInGrid(scope, e.detail)) e.preventDefault();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
